import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Modal,
  Select,
  Space,
  Table,
  TableProps,
  message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { appSelector } from "../../helpers/appSelector";
import { AppDispatch } from "../../helpers/appDispatch";
import {
  assignStudentToRoom,
  unassignStudentFromRoom,
} from "../../store/studentRoomAssignments/actions";
import { getStudentsRequest } from "../../store/students";
import { getCurrentAcademicYears } from "../../helpers/functions";
import { Student, HallRoom } from "../../interfaces";

interface Props {
  room: HallRoom;
  students: Student[];
  loading: boolean;
  schoolId: string;
  roomAssignments: Student[];
  loadingRoomAssignments: boolean;
}

const AssignStudentToRoom: React.FC<Props> = ({
  room,
  students,
  loading,
  schoolId,
  roomAssignments,
  loadingRoomAssignments,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState<string>("");
  const { currentYear } = getCurrentAcademicYears();

  // Generate academic year options dynamically
  const generateAcademicYearOptions = (): string[] => {
    const options: string[] = [];
    for (let i = -2; i <= 2; i++) {
      const year = currentYear + i;
      const next = year + 1;
      options.push(`${year}-${next}`);
    }
    return options;
  };

  const academicYearOptions = generateAcademicYearOptions();
  const [modalAcademicYear, setModalAcademicYear] = useState<string>(
    `${currentYear}-${currentYear + 1}`
  );

  const { isAssigning, isUnassigning, success } = appSelector(
    (state) => state.studentRoomAssignmentStore
  );

  useEffect(() => {
    if (isModalOpen && modalAcademicYear && schoolId) {
      dispatch(
        getStudentsRequest({
          school_id: schoolId,
          academic_year: modalAcademicYear,
        })
      );
    }
  }, [dispatch, isModalOpen, modalAcademicYear, schoolId]);

  useEffect(() => {
    if (success) {
      setIsModalOpen(false);
      form.resetFields();
      setSelectedStudentId("");
      // Room assignments will be refreshed by parent component
    }
  }, [success, form]);

  const handleAssignStudent = () => {
    if (!selectedStudentId) {
      message.error("Please select a student");
      return;
    }

    dispatch(
      assignStudentToRoom({
        student_id: selectedStudentId,
        room_id: room.id.toString(),
      })
    );
  };

  const handleUnassignStudent = (studentId: string | number) => {
    dispatch(unassignStudentFromRoom({ student_id: studentId }));
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
    setSelectedStudentId("");
  };

  // Filter out students who are already assigned to this room
  const availableStudents = students.filter(
    (student) => !roomAssignments.some((assigned) => assigned.id === student.id)
  );

  const columns: TableProps<Student>["columns"] = [
    {
      title: "Application Number",
      dataIndex: "application_number",
      key: "application_number",
    },
    {
      title: "Name",
      render: (record: Student) => `${record.surname} ${record.other_names}`,
      key: "name",
    },
    {
      title: "Programme",
      dataIndex: "programme",
      key: "programme",
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
    },
    {
      title: "Actions",
      key: "actions",
      render: (record: Student) => (
        <Button
          type="link"
          danger
          onClick={() => handleUnassignStudent(record.id)}
          loading={isUnassigning}
        >
          Unassign
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsModalOpen(true)}
        >
          Assign Student
        </Button>
      </Space>

      <Table
        columns={columns}
        dataSource={roomAssignments}
        loading={loadingRoomAssignments}
        rowKey="id"
        pagination={{ pageSize: 10 }}
        scroll={{ x: true }}
      />

      <Modal
        title={`Assign Student to ${room.name}`}
        open={isModalOpen}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="assign"
            type="primary"
            loading={isAssigning}
            onClick={handleAssignStudent}
          >
            Assign
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Academic Year"
            rules={[
              { required: true, message: "Please select an academic year" },
            ]}
          >
            <Select
              value={modalAcademicYear}
              onChange={setModalAcademicYear}
              style={{ width: "100%" }}
            >
              {academicYearOptions.map((year) => (
                <Select.Option key={year} value={year}>
                  {year}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Select Student"
            rules={[{ required: true, message: "Please select a student" }]}
          >
            <Select
              placeholder="Select a student"
              value={selectedStudentId}
              onChange={setSelectedStudentId}
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string)
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {availableStudents.map((student) => (
                <Select.Option key={student.id} value={student.id}>
                  {student.application_number} - {student.surname}{" "}
                  {student.other_names}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AssignStudentToRoom;
